import axios from 'axios';
import { supabase } from './supabase';
import { apiKeyService } from './apiKeyService';

// URL de l'API Emelia.io
const EMELIA_API_URL = 'https://api.emelia.io';

// Clé API par défaut (sera remplacée par celle de la base de données si disponible)
let EMELIA_API_KEY = '';

// Fonction pour créer ou mettre à jour l'instance axios avec la dernière clé API
const getEmeliaApi = async () => {
  // Récupérer la clé API depuis le service apiKeyService
  const apiKey = await apiKeyService.getApiKey('emelia');
  
  // Si une clé API a été trouvée, la mettre en cache
  if (apiKey) {
    EMELIA_API_KEY = apiKey;
  }
  
  // Retourner une instance axios configurée avec la clé API actuelle
  return axios.create({
    baseURL: EMELIA_API_URL,
    headers: {
      Authorization: EMELIA_API_KEY,
      'Content-Type': 'application/json'
    }
  });
};

/**
 * Interface pour les campagnes d'emails
 */
export interface EmailCampaign {
  id: string;
  name: string;
  template: string;
  sent_count: number;
  open_count: number;
  click_count: number;
  created_at: string;
}

/**
 * Interface pour les contacts
 */
export interface Contact {
  firstName: string;
  lastName: string;
  email: string;
  customFields?: Record<string, any>;
}

/**
 * Récupère toutes les campagnes d'emails
 */
export const getCampaigns = async (): Promise<any> => {
  try {
    const api = await getEmeliaApi();
    const response = await api.get('/emails/campaigns');
    return response.data;
  } catch (error) {
    console.error('Error fetching Emelia campaigns:', error);
    throw error;
  }
};

/**
 * Crée une nouvelle campagne d'emails
 */
export const createCampaign = async (name: string, template: string): Promise<any> => {
  try {
    const api = await getEmeliaApi();
    const response = await api.post('/emails/campaigns', {
      name,
      template
    });
    
    // Enregistrer la campagne dans Supabase
    await supabase.from('email_campaigns').insert({
      emelia_id: response.data.id,
      name,
      template,
      sent_count: 0,
      open_count: 0,
      click_count: 0
    });
    
    return response.data;
  } catch (error) {
    console.error('Error creating Emelia campaign:', error);
    throw error;
  }
};

/**
 * Ajoute un prospect à une campagne
 */
export const addProspectToCampaign = async (campaignId: string, prospect: any): Promise<any> => {
  try {
    const api = await getEmeliaApi();
    const contact: Contact = {
      firstName: prospect.firstName,
      lastName: prospect.lastName,
      email: prospect.email,
      customFields: {
        company: prospect.company || '',
        token: prospect.token || ''
      }
    };
    
    const response = await api.post('/contacts', contact);
    const addToCampaignResponse = await api.post(`/emails/campaigns/${campaignId}/contacts`, {
      contactIds: [response.data.id]
    });
    
    // Mettre à jour le prospect dans Supabase
    await supabase
      .from('prospects')
      .update({ 
        email_campaign_id: campaignId,
        email_sent: true,
        email_sent_at: new Date().toISOString()
      })
      .eq('id', prospect.id);
    
    return addToCampaignResponse.data;
  } catch (error) {
    console.error('Error adding prospect to Emelia campaign:', error);
    throw error;
  }
};

/**
 * Configure un webhook pour suivre les interactions
 */
export const setupWebhook = async (eventType: string, url: string): Promise<any> => {
  try {
    const api = await getEmeliaApi();
    const response = await api.post('/webhooks', {
      event: eventType,
      url: url
    });
    return response.data;
  } catch (error) {
    console.error('Error setting up Emelia webhook:', error);
    throw error;
  }
};

/**
 * Récupère tous les webhooks configurés
 */
export const getWebhooks = async (): Promise<any> => {
  try {
    const api = await getEmeliaApi();
    const response = await api.get('/webhooks');
    return response.data;
  } catch (error) {
    console.error('Error fetching Emelia webhooks:', error);
    throw error;
  }
};

/**
 * Recherche l'email d'un contact
 */
export const findEmail = async (firstName: string, lastName: string, domain: string): Promise<any> => {
  try {
    const api = await getEmeliaApi();
    const response = await api.post('/email-finder/find', {
      firstName,
      lastName,
      domain
    });
    return response.data;
  } catch (error) {
    console.error('Error finding email with Emelia:', error);
    throw error;
  }
};

/**
 * Récupère le résultat d'une recherche d'email
 */
export const getEmailFindResult = async (jobId: string): Promise<any> => {
  try {
    const api = await getEmeliaApi();
    const response = await api.get(`/email-finder/jobs/${jobId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting email find result from Emelia:', error);
    throw error;
  }
};

/**
 * Traite les événements de webhook d'Emelia
 */
export const processWebhookEvent = async (event: any): Promise<void> => {
  try {
    const { eventType, data } = event;
    
    // Extraire le token du customField
    const token = data.contact?.customFields?.token;
    if (!token) return;
    
    // Récupérer le prospect associé au token
    const { data: prospect, error } = await supabase
      .from('prospects')
      .select('*')
      .eq('token', token)
      .single();
    
    if (error || !prospect) {
      console.error('Prospect not found for token:', token);
      return;
    }
    
    // Mettre à jour le prospect en fonction de l'événement
    switch (eventType) {
      case 'email.opened':
        await supabase
          .from('prospects')
          .update({ 
            email_opened: true,
            email_opened_at: new Date().toISOString()
          })
          .eq('id', prospect.id);
        break;
      
      case 'email.clicked':
        await supabase
          .from('prospects')
          .update({ 
            email_clicked: true,
            email_clicked_at: new Date().toISOString(),
            last_click_url: data.url
          })
          .eq('id', prospect.id);
        break;
      
      case 'email.replied':
        await supabase
          .from('prospects')
          .update({ 
            email_replied: true,
            email_replied_at: new Date().toISOString()
          })
          .eq('id', prospect.id);
        break;
    }
    
    // Mettre à jour les statistiques de la campagne
    if (data.campaignId) {
      const { data: campaign } = await supabase
        .from('email_campaigns')
        .select('*')
        .eq('emelia_id', data.campaignId)
        .single();
      
      if (campaign) {
        let updateData = {};
        
        switch (eventType) {
          case 'email.opened':
            updateData = { open_count: campaign.open_count + 1 };
            break;
          
          case 'email.clicked':
            updateData = { click_count: campaign.click_count + 1 };
            break;
        }
        
        await supabase
          .from('email_campaigns')
          .update(updateData)
          .eq('id', campaign.id);
      }
    }
  } catch (error) {
    console.error('Error processing Emelia webhook event:', error);
  }
};
